import { OmniElement, OmniStyle, OmniIcon, html, css, nothing } from 'omni-ui';

OmniStyle.register();
OmniIcon.register();

/**
 * Depending on the message type, extract a viewable string or lit template to display.
 * Special cases for "Response" objects from fetch and "Error" exception objects. Other
 * objects will be serialized to a JSON string.
 *
 * @async
 * @returns {Promise<string | lit-template>} The message to render
 */
const extractMessage = async obj => {
  if (obj === '' || obj === null || obj === undefined) return html`&nbsp;`;
  if (obj !== null && typeof obj === 'object') {
    if (obj._$litType$) return obj; // lit template
    if (obj instanceof Response) {
      try {
        const txt = await obj.text();
        try {
          const json = JSON.parse(txt);
          return extractMessage(
            json.errors ?? json.error ?? json.message ?? json.detail ?? json
          );
        } catch {
          return `${obj.status}: ${txt}`;
        }
      } catch {
        return `${obj.status}: ${obj.statusText}`;
      }
    }
    if (obj instanceof Error && obj.message) {
      return `${obj.name}: ${obj.message}`;
    }
    try {
      return html`
        <span style="white-space:pre">${JSON.stringify(obj, null, 4)}</span>
      `;
    } catch {
      return obj;
    }
  }
  return obj;
};

/**
 * Base implementation for both om2alert and om2confirm which slight differences
 * around default (& override) options.
 *
 * @async
 * @param {array} args - The arguments passed to om2alert/om2confirm
 * @param {object} [defaultOpts] - Default options
 * @param {object} [overrideOpts] - Override options
 * @returns {Promise} Will resolve when the modal closes
 */
const createModal = async (args, defaultOpts = {}, overrideOpts = {}) => {
  let options;
  const lastArg = args[args.length - 1];
  if (args.length > 1 && lastArg?.constructor === Object) {
    options = { ...defaultOpts, ...args.pop(), ...overrideOpts };
  } else {
    options = { ...defaultOpts, ...overrideOpts };
  }
  const messages = args;
  const messagesAsArray = Array.isArray(messages) ? messages : [messages];
  const message = await Promise.all(
    messagesAsArray.map(msg => extractMessage(msg))
  );
  return new Promise(resolve => {
    const elem = document.createElement('om2-alert');
    document.body.appendChild(elem);
    let closeList = [resolve];
    if (options.close) {
      if (Array.isArray(options.close)) {
        closeList = [...options.close, ...closeList];
      } else {
        closeList.push(options.close);
      }
    }
    const closeButton = options.closeButton ?? !options.callback;
    elem.dispatchEvent(
      new CustomEvent('om2alert', {
        detail: { message, ...options, close: closeList, closeButton },
      })
    );
  });
};

/**
 * A replacement for window.alert() using an omni modal with appropriate color
 * and icon. Supports multiple types: 'info', 'success', 'warning', 'danger'.
 * 'danger' is the default for om2alert.
 */
export const om2alert = async (...args) =>
  createModal(args, { type: 'danger' }, { callback: undefined });

/**
 * A replacement for window.confirm() using an omni modal with appropriate color
 * and icon. Supports multiple types: 'info', 'success', 'warning', 'danger'.
 * 'info' is the default for om2alert.
 */
export const om2confirm = async (...args) =>
  createModal(args, {
    callback: () => {},
    type: 'info',
    title: 'Confirm',
    hideCancel: false,
  });

class Om2Alert extends OmniElement {
  constructor() {
    super();
    this.type = undefined; // info, success, warning, danger
    this.message = '';
    this.styling = '';
    this.title = '';
    this.ident = this.constructor.idCounter ?? 0;
    this.constructor.idCounter = this.ident + 1;
    this.triggerRender = this.triggerRender.bind(this);
    this.closeOnModalClick = true;

    this.addEventListener('om2alert', e => {
      this.message = e.detail.message;
      this.type = e.detail.type;
      this.callback = e.detail.callback;
      this.styling = e.detail.styling || '';
      this.title = e.detail.title;
      this.close = e.detail.close;
      this.closeTimeout = e.detail.closeTimeout;
      this.hideCancel = e.detail.hideCancel;
      this.hideOverlay = !!e.detail.hideOverlay;
      this.closeOnModalClick = !!(e.detail.closeOnModalClick ?? true);
      this.closeButton = !!e.detail.closeButton;
      this.toast = e.detail.toast;
      this.modalWidth = e.detail.modalWidth;
      if (this.toast && !this.closeTimeout) {
        this.closeTimeout = 2000;
      }
      if (this.closeTimeout) {
        setTimeout(() => this.closeModal(), this.closeTimeout);
      }
    });

    if (!this.constructor.topOffsets) {
      // static field initialization
      this.constructor.ident = 0;
      this.constructor.topOffsets = {};
    }
  }

  static get properties() {
    return {
      type: { type: String },
      message: { type: String },
      title: { type: String },
      /* This can be set to a single callback function or an array of callback
       * objects (multiple buttons). Callback objects are of the form:
       *   { func: myFunction, label: 'Click Here' }
       */
      callback: { attribute: false },
      close: { attribute: false },
      hideOverlay: { type: Boolean, reflect: true },
      trigger: { state: true },
      isBusy: { state: true },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .omni .modal-content.no-modal {
          position: fixed;
          z-index: 41;
          overflow: hidden;
          left: 0;
          right: 0;

          top: 5.7rem;
          transition-property: top;
          transition-duration: 0.4s;

          opacity: 1;
          transition-property: opacity;
          transition-duration: 0.4s;
        }

        .omni .modal-content.no-modal .notification {
          padding-top: 13px;
          padding-bottom: 13px;
        }

        .omni .modal-content article > span {
          overflow-wrap: anywhere;
        }

        :host([hideoverlay]) .modal-background {
          background-color: rgb(0, 0, 0, 0); // transparent
        }

        /* Try to avoid weird bug in firefox with transform-style: preserve-3d */
        @-moz-document url-prefix() {
          .omni .button {
            transform-style: unset !important;
          }

          .omni .button.is-outlined::before {
            background-image: none !important;
          }

          .omni .button.is-link.is-outlined.is-focused,
          .omni .button.is-link.is-outlined.is-hovered,
          .omni .button.is-link.is-outlined:focus,
          .omni .button.is-link.is-outlined:hover {
            background-color: rgb(var(--rgb-primary)) !important;
          }
        }
      `,
    ];
  }

  static register(name = 'om2-alert') {
    return super.register(name, Om2Alert);
  }

  triggerRender() {
    this.trigger = !this.trigger;
  }

  shouldUpdate() {
    // Handle case where this.callback is not an array, transform appropriately
    if (this.callback && !Array.isArray(this.callback)) {
      this.callback = [{ func: this.callback, label: 'Ok' }];
    }
    return true;
  }

  iconId() {
    return {
      info: 'omni:informative:info',
      success: 'omni:informative:success',
      warning: 'omni:informative:alert',
      danger: 'omni:informative:error',
    }[this.type];
  }

  async closeModal() {
    if (this.toast) {
      // Set opacity to trigger a css transition; delay removal so it can animate.
      this.shadowRoot.querySelector('div.no-modal').style.opacity = 0;
      await new Promise(r => {
        setTimeout(r, 500);
      });

      // For no-modal notifications, clean up static datastructure
      const index = this.constructor.positions.findIndex(
        pos => pos.ident === this.ident
      );
      this.constructor.positions.splice(index, 1);
      this.constructor.pubsub.removeEventListener('change', this.triggerRender);
      this.constructor.pubsub.dispatchEvent(new CustomEvent('change'));
    }

    this.remove(); // seppuku
    if (this.close) {
      // Handle single close callback or array of callbacks
      (Array.isArray(this.close) ? this.close : [this.close]).forEach(
        callback => callback()
      );
    }
  }

  renderIcon() {
    const iconId = this.iconId();
    return iconId ? html`<omni-icon icon-id=${iconId}></omni-icon>` : '';
  }

  renderCloseButton() {
    return html`
      <button
        style="${this.type === 'white'
          ? '--color-white: var(--color-almost-black)'
          : ''}"
        class="delete ${this.closeButton ? '' : 'is-hidden'}"
        aria-label="close"
        @click=${() => {
          this.closeModal();
        }}
      ></button>
    `;
  }

  renderMessage() {
    return html`
      <article class="message is-${this.type}">
        <div class="message-header">
          <p>${this.renderIcon()} ${this.title}</p>
          ${this.renderCloseButton()}
        </div>
        <div class="message-body" style="${this.styling}">${this.message}</div>
        <div class="message-footer">
          <div class="field is-grouped is-grouped-right">
            <div class="control ${this.hideCancel ? 'is-hidden' : ''}">
              <button
                class="button is-text is-${this.type}"
                @click=${() => {
                  this.closeModal();
                }}
              >
                Cancel
              </button>
            </div>
            ${this.callback.map(
              ({ func, label, cls }) => html`
                <div class="control">
                  <button
                    class="button ${cls ?? `is-${this.type}`} ${this.isBusy ===
                    func
                      ? 'is-loading is-primary'
                      : 'is-outlined'}"
                    ?disabled=${this.isBusy && this.isBusy !== func}
                    @click=${async () => {
                      this.isBusy = func;
                      await func(); // Wait for the callback to complete before closing
                      this.closeModal();
                    }}
                  >
                    ${label}
                  </button>
                </div>
              `
            )}
          </div>
        </div>
      </article>
    `;
  }

  renderNotification() {
    return html` <article class="notification is-${this.type}">
      ${this.renderIcon()} ${this.renderCloseButton()}
      <span style="${this.styling}">${this.message}</span>
    </article>`;
  }

  renderToastNotification() {
    if (!this.constructor.positions) {
      // Init static fields used to position notifications above each other
      this.constructor.positions = [];
      this.constructor.pubsub = document.createElement('dummy');
    }

    let visibility = 'unset';
    let index = this.constructor.positions.findIndex(
      pos => pos.ident === this.ident
    );
    if (index < 0) {
      // First time rendering: Add self to positions list & listen for changes
      index = this.constructor.positions.length;
      this.constructor.positions.push({
        ident: this.ident,
        height: 0,
      });
      this.constructor.pubsub.addEventListener('change', this.triggerRender);
      visibility = 'hidden'; // Hide until we know where to position (next tick)
      setTimeout(this.triggerRender, 0);
    } else {
      const positionData = this.constructor.positions[index];
      if (!positionData.height) {
        // Second time rendering, we can determine our height and update
        positionData.height =
          this.shadowRoot.querySelector('div.no-modal').offsetHeight;
        this.constructor.pubsub.dispatchEvent(new CustomEvent('change'));
      }
    }

    // Calculate top offset by adding up all previous notification heights (+ a buffer)
    const buffer = 1;
    let topRem = 5.7;
    for (let i = 0; i < index; i += 1) {
      topRem += this.constructor.positions[i].height / 14 + buffer;
    }

    return html`
      <div
        class="no-modal modal-content"
        style="top: ${topRem}rem; visibility: ${visibility}; ${this.modalWidth
          ? `width: ${this.modalWidth}`
          : ''}"
        @click=${() => {
          this.closeModal();
        }}
        @keyup=${e => {
          if (e.key === 'Escape') this.closeModal();
        }}
      >
        <article class="notification is-${this.type}">
          ${this.renderIcon()}
          <span style="${this.styling}">${this.message}</span>
        </article>
      </div>
    `;
  }

  renderContainer() {
    if (this.toast) {
      return this.renderToastNotification();
    }
    return html`
        <div class="modal is-active">
          <div class="modal-background"
            @click=${() => this.closeOnModalClick && this.closeModal()}
            role="presentation"
            ></div>
            <div
              class="modal-content"
              style=${this.modalWidth ? `width: ${this.modalWidth}` : nothing}>
              ${
                this.callback ? this.renderMessage() : this.renderNotification()
              }
            </div>
          </div>
        </div>
    `;
  }

  render() {
    return html` <omni-style> ${this.renderContainer()} </omni-style> `;
  }
}

Om2Alert.register();
